export interface ManualTaxMapping {
  /**
   * Manual tax mapping ID.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Revision number, which increments by 1 each time the manual tax mapping is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the manual tax mapping.
   *
   * Ignored when creating a manual tax mapping.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the manual tax mapping was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the manual tax mapping was last updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * Tax group ID. Tax groups define collections of items or services that share the same tax treatment.
   * @format GUID
   */
  taxGroupId?: string | null;
  /**
   * Tax region ID. Tax regions represent geographical areas where specific tax rules apply.
   * @format GUID
   */
  taxRegionId?: string | null;
  /**
   * Tax rate to apply for this tax group and region combination.
   *
   * Specified as a decimal string representing a fraction, not a percentage. For example, `"0.05"` is 5%, `"0.1"` is 10%, and `"0.08"` is 8%. Don't pass a number (`0.05`), an integer (`5`), or a percentage string (`"5%"`). Up to 6 decimal places are supported.
   * @decimalValue options { maxScale:6, gte:0 }
   */
  taxRate?: string;
  /**
   * Description of the manual tax mapping. Use this field to add context or notes about the tax rate.
   * @maxLength 1000
   */
  description?: string | null;
  /**
   * Type of tax being applied, such as `VAT`, `GST`, or `Sales Tax`. This depends on the jurisdiction's tax laws.
   * @maxLength 200
   */
  taxType?: string;
  /**
   * Customer-facing name of the tax, shown at checkout. For example, `STATE TAX`, `GST`, or `VAT`.
   *
   * Cosmetic only. It doesn't affect the calculated amount. Can be left empty, in which case checkout shows a default label such as `Sales Tax`.
   * @maxLength 200
   */
  taxName?: string;
  /**
   * Name of the jurisdiction where this tax rate applies. For example, `California` or `United Kingdom`.
   * @maxLength 200
   */
  jurisdiction?: string;
  /** Type of jurisdiction where this tax rate applies, such as country, state, county, city, or special district. */
  jurisdictionType?: JurisdictionTypeWithLiterals;
  /**
   * Tax exemption details for user-defined exempt groups.
   * @maxSize 50
   */
  exemptRates?: ExemptDetails[];
  /** Extended fields for storing additional custom data. */
  extendedFields?: ExtendedFields;
}

/** Type of jurisdiction that taxes were calculated for. For example, `"State"`, `"County"`, `"City"`, `"Special"`, etc. */
export enum JurisdictionType {
  UNDEFINED = 'UNDEFINED',
  COUNTRY = 'COUNTRY',
  STATE = 'STATE',
  COUNTY = 'COUNTY',
  CITY = 'CITY',
  SPECIAL = 'SPECIAL',
}

/** @enumType */
export type JurisdictionTypeWithLiterals =
  | JurisdictionType
  | 'UNDEFINED'
  | 'COUNTRY'
  | 'STATE'
  | 'COUNTY'
  | 'CITY'
  | 'SPECIAL';

export interface ExemptDetails {
  /**
   * Tax exempt group ID.
   * @format GUID
   */
  taxExemptGroupId?: string;
  /**
   * Tax override rate for given tax mapping.
   * @decimalValue options { maxScale:6, gte:0 }
   */
  taxRate?: string;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface CreateManualTaxMappingRequest {
  /** Manual tax mapping to create. */
  manualTaxMapping: ManualTaxMapping;
}

export interface CreateManualTaxMappingResponse {
  /** Created manual tax mapping. */
  manualTaxMapping?: ManualTaxMapping;
}

export interface BulkCreateManualTaxMappingsRequest {
  /**
   * Manual tax mappings to create.
   * @minSize 1
   * @maxSize 100
   */
  manualTaxMappings: ManualTaxMapping[];
  /**
   * Whether to return the created manual tax mapping entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
}

export interface BulkCreateManualTaxMappingsResponse {
  /**
   * Results of the bulk create operation, including created manual tax mappings and metadata.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkManualTaxMappingResult[];
  /** Metadata for the bulk create operation. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Provided only whenever possible. For example, `itemId` can't be provided when item creation has failed.
   * @format GUID
   */
  id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action for this item was successful. When `false`, the `error` field is returned. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkManualTaxMappingResult {
  /** Metadata for the individual create operation. */
  itemMetadata?: ItemMetadata;
  /** Created manual tax mapping. Only present if `returnEntity` was set to `true` in the request. */
  item?: ManualTaxMapping;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface GetManualTaxMappingRequest {
  /**
   * Manual tax mapping ID.
   * @format GUID
   */
  manualTaxMappingId: string;
}

export interface GetManualTaxMappingResponse {
  /** Retrieved manual tax mapping. */
  manualTaxMapping?: ManualTaxMapping;
}

export interface UpdateManualTaxMappingRequest {
  /** Manual tax mapping to update. */
  manualTaxMapping: ManualTaxMapping;
  /**
   * Set of fields to update.
   *
   * Fields that aren't included in `fieldMask.paths` are ignored.
   * @internal
   */
  fieldMask?: string[];
}

export interface UpdateManualTaxMappingResponse {
  /** Updated manual tax mapping. */
  manualTaxMapping?: ManualTaxMapping;
}

export interface DeleteManualTaxMappingRequest {
  /**
   * Manual tax mapping ID.
   * @format GUID
   */
  manualTaxMappingId: string;
}

export interface DeleteManualTaxMappingResponse {}

export interface QueryManualTaxMappingsRequest {
  /** Query options. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section).
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 50
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryManualTaxMappingsResponse {
  /**
   * Manual tax mappings that match the query criteria.
   * @maxSize 100
   */
  manualTaxMappings?: ManualTaxMapping[];
  /** Pagination metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface UpdateExtendedFieldsRequest {
  /** ID of the entity to update. */
  id: string;
  /** Identifier for the app whose extended fields are being updated. */
  namespace: string;
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData: Record<string, any> | null;
}

export interface UpdateExtendedFieldsResponse {
  /** Updated manual tax mapping. */
  manualTaxMapping?: ManualTaxMapping;
}

export interface BulkRestoreManualTaxMappingsRequest {
  /**
   * Manual tax mapping IDs to restore.
   * @format GUID
   * @minSize 1
   * @maxSize 200
   */
  manualTaxMappingIds?: string[];
}

export interface BulkRestoreManualTaxMappingsResponse {
  /**
   * Restored manual tax mappings
   * @minSize 1
   * @maxSize 200
   */
  results?: BulkRestoreManualTaxMappingsResult[];
}

export interface BulkRestoreManualTaxMappingsResult {
  itemMetadata?: ItemMetadata;
}

export interface BulkAssignExemptOverridesRequest {
  /**
   * List of assign overrides.
   * @maxSize 50
   */
  assignOverrides?: AssignOverride[];
  /**
   * Whether to return the updated manual tax mapping entities in the response.
   * Default: `false`
   */
  returnEntity?: boolean;
}

export interface AssignOverride {
  /**
   * ID of the manual tax mapping to which the exempt group override will be assigned.
   * @format GUID
   */
  manualTaxMappingId?: string;
  /**
   * List of exempt group overrides to assign to the manual tax mapping.
   * @minSize 1
   * @maxSize 50
   */
  exemptGroupOverrides?: ExemptGroupIdOverride[];
}

export interface ExemptGroupIdOverride {
  /**
   * ID of the tax exempt group.
   * @format GUID
   */
  exemptGroupId?: string;
  /**
   * Override tax rate for the exempt group in the context of the manual tax mapping.
   * @format DECIMAL_VALUE
   * @decimalValue options { maxScale:3 }
   */
  rate?: string;
}

export interface BulkAssignExemptOverridesResponse {
  /**
   * List of updated manual tax mappings.
   * @maxSize 50
   */
  results?: BulkExemptOverrideResult[];
  /** Metadata for the bulk assign operation. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkExemptOverrideResult {
  /** Metadata for the individual assign operation. */
  itemMetadata?: ItemMetadata;
  /** Updated manual tax mapping. Only present if `returnEntity` was set to `true` in the request. */
  item?: ManualTaxMapping;
}

export interface BulkUnassignExemptOverridesRequest {
  /**
   * List of unassign overrides.
   * @maxSize 50
   */
  unassignOverrides: UnassignOverride[];
  /**
   * Whether to return the updated manual tax mapping entities in the response.
   * Default: `false`
   */
  returnEntity?: boolean;
}

export interface UnassignOverride {
  /**
   * ID of the manual tax mapping from which the exempt group override will be unassigned.
   * @format GUID
   */
  manualTaxMappingId?: string;
  /**
   * List of exempt group IDs to unassign from the manual tax mapping.
   * @format GUID
   * @minSize 1
   * @maxSize 50
   */
  exemptGroupIds?: string[];
}

export interface BulkUnassignExemptOverridesResponse {
  /**
   * List of updated manual tax mappings.
   * @maxSize 10
   */
  results?: BulkExemptOverrideResult[];
  /** Metadata for the bulk assign operation. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ListManualTaxMappingsRequest {}

export interface ListManualTaxMappingsResponse {
  /**
   * List of manual tax mappings.
   * @maxSize 1000
   */
  manualTaxMappings?: ManualTaxMapping[];
  /** Pagination metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /**
   * Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity
   * @internal
   */
  triggeredByUndelete?: boolean | null;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
  /**
   * This field is currently part of the of the EntityUpdatedEvent msg, but scala/node libraries which implements the domain events standard
   * wont populate it / have any reference to it in the API.
   * The main reason for it is that fetching the old entity from the DB will have a performance hit on an update operation so unless truly needed,
   * the developer should send only the new (current) entity.
   * An additional reason is not wanting to send this additional entity over the wire (kafka) since in some cases it can be really big
   * Developers that must reflect the old entity will have to implement their own domain event sender mechanism which will follow the DomainEvent proto message.
   * @internal
   * @deprecated
   */
  previousEntityAsJson?: string | null;
  /**
   * Map of fully qualified field paths to their previous values for fields that changed.
   * For more details please see [AIP](https://dev.wix.com/docs/rnd-general/articles/p13n-guidelines-aips/guidance-aips/design-patterns/7016-events#modified-fields-format)
   * @internal
   */
  modifiedFields?: Record<string, any>;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface EntityDeletedEvent {
  /**
   * Indicates if the entity is sent to trash-bin. only available when trash-bin is enabled
   * @internal
   */
  movedToTrash?: boolean | null;
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface Empty {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CreateManualTaxMappingApplicationErrors =
  | {
      code?: 'TAX_REGION_ID_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'TAX_GROUP_ID_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'MANUAL_TAX_MAPPING_ALREADY_EXISTS';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type UpdateManualTaxMappingApplicationErrors =
  | {
      code?: 'MANUAL_TAX_MAPPING_ALREADY_EXISTS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'TAX_REGION_ID_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'TAX_GROUP_ID_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    };
